import React from 'react'
import TextInput from '../../components/FormElements/TextInput'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { useModal } from '../../components/useModal'
import { toast } from 'react-hot-toast'
import { useGetAllUsers } from '../../api/admin/useGetAllUsers'
import { motion } from 'framer-motion'
import { ReactComponent as RemoveIcon } from '../../images/icons/remove.svg'
import { useGetDealerships } from '../../api/admin/useGetDealerships'
import { useCreateAgency } from '../../api/admin/useCreateAgency'
import { AsyncDualSelect, ToggleInput, Tooltip, Button } from '@demandlocal/components'
import { queryClient } from '../../api/queryClient'

const AdminAddAgency = (): JSX.Element => {
  const { closeModal } = useModal()
  const formContext = useForm({
    mode: 'onChange',
  })

  const { watch, setValue, getValues } = formContext

  const assignedUsersValues = watch(`assigned_users`)
  const assignedDealershipsValues = watch(`assigned_dealerships`)

  const { mutateAsync } = useCreateAgency()

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = formContext

  const stripeEnabled = watch('stripe_enabled')

  const onSubmit = (values: Partial<FieldValues>): unknown => {
    return mutateAsync(
      {
        data: {
          ...values,
          stripe_enabled: !!values?.stripe_enabled,
          assigned_users: values?.assigned_users?.map((user) => user.value),
          assigned_dealerships: values?.assigned_dealerships?.map(
            (dealership) => dealership.value,
          ),
        },
      },
      {
        onSuccess: () => {
          closeModal()
          queryClient.refetchQueries(['admin', 'organizations'], { active: true })
          toast.success(`Agency ${values?.name} created`)
        },
      },
    )
  }

  return (
    <FormProvider {...formContext}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={'flex flex-1 flex-col px-12 pt-12 font-Spartan h-full'}>
        <div className={'flex flex-1 flex-col overflow-auto'}>
          <div className={'font-bold text-blue-primary text-[28px]'}>Add new agency</div>
          <div className={'pt-8 pb-8 gap-2 border-b-2'}>
            <div>
              <TextInput
                rules={{ required: 'This field is required' }}
                inverted
                placeholder={'Agency name'}
                name={'name'}
                label={'Agency name'}
                control={control}
              />
            </div>
            <div className={'max-w-sm'}>
              <Tooltip label={'Enabling this will show RSV data on Mobile reports'}>
                <div className={'flex flex-row gap-4 items-center'}>
                  <ToggleInput name={'show_rsv'} label={'Show walk ins'} />
                </div>
              </Tooltip>
            </div>
          </div>
          <div className={'pt-8 flex flex-col gap-10 border-b-2 pb-8'}>
            <div className={'max-w-sm'}>
              <Tooltip label={'Enable Stripe payments'}>
                <div className={'flex flex-row gap-4 items-center'}>
                  <ToggleInput name={'stripe_enabled'} label={'Enable Stripe payments'} />
                </div>
              </Tooltip>
            </div>
            {stripeEnabled && (
              <div className={'grid grid-cols-2 gap-4'}>
                <TextInput
                  inverted
                  placeholder={'cus_xxxxxxxxxx'}
                  name={'stripe_customer_id'}
                  label={'Stripe Customer ID'}
                  info={'Auto-created if not provided'}
                  control={control}
                />
                <TextInput
                  inverted
                  placeholder={'Billing Email'}
                  name={'billing_email'}
                  label={'Billing Email'}
                  control={control}
                />
              </div>
            )}
          </div>
          <div className={'pt-8 pb-8 border-b-2'}>
            <div className={'flex flex-row'}>
              <div className={'flex flex-col pr-5'}>
                <label
                  className={`block text-blue-primary text-sm font-Spartan font-bold mb-2`}
                  htmlFor={'assigned_dealerships'}>
                  Assign dealerships
                </label>
                <div className={'flex flex-shrink-0 mt-4'}>
                  <div className={'flex flex-shrink-0 mt-4'}>
                    <AsyncDualSelect
                      rules={{ required: 'This field is required' }}
                      optionsListTitle={'Choose dealership'}
                      selectedOptionsListTitle={'Selected dealerships'}
                      buttonTitle={'+ Assign dealership'}
                      control={control}
                      fetchFn={useGetDealerships}
                      labelKeyName={'dealership'}
                      valueKeyName={'id'}
                      searchKeyName={'dealership'}
                      name={`assigned_dealerships`}
                    />
                  </div>
                </div>
              </div>
              {assignedDealershipsValues?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className={
                    'pl-5 flex flex-row flex-1 flex-wrap justify-start items-end gap-3 border-l-2'
                  }>
                  {assignedDealershipsValues?.map((value, index: number) => {
                    return (
                      <div
                        key={value.value}
                        className={
                          'flex flex-col p-2 bg-white border-2 min-w-32 rounded-lg min-w-[120px]'
                        }>
                        <div className={'flex justify-between text-xs'}>
                          <span className={'font-bold'}>{value.label}</span>
                          <RemoveIcon
                            className={'cursor-pointer ml-2'}
                            onClick={(e) => {
                              e.preventDefault()
                              setValue(
                                `assigned_dealerships`,
                                getValues(`assigned_dealerships`).filter(
                                  (v) => v.value !== value.value,
                                ),
                              )
                            }}
                          />
                        </div>
                        <span className={'font-medium pt-1 text-xs'}>{value.value}</span>
                      </div>
                    )
                  })}
                </motion.div>
              )}
            </div>
          </div>
          <div className={'pt-8 pb-8'}>
            <div className={'flex flex-row'}>
              <div className={'flex flex-col pr-5'}>
                <label
                  className={`block text-blue-primary text-sm font-Spartan font-bold mb-2`}
                  htmlFor={'assigned_dealerships'}>
                  Assign users
                </label>
                <div className={'flex flex-shrink-0 mt-4'}>
                  <AsyncDualSelect
                    optionsListTitle={'Choose user'}
                    selectedOptionsListTitle={'Selected users'}
                    buttonTitle={'+ Assign users'}
                    control={control}
                    valueKeyName={'id'}
                    labelKeyName={['first_name', 'last_name']}
                    searchKeyName={['first_name', 'last_name']}
                    customLabelFormat={'{0} {1}'}
                    fetchFn={useGetAllUsers}
                    name={`assigned_users`}
                  />
                </div>
              </div>
              {assignedUsersValues?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className={
                    'pl-5 flex flex-row flex-1 flex-wrap justify-start items-end gap-3 border-l-2'
                  }>
                  {assignedUsersValues?.map((value, index: number) => {
                    return (
                      <div
                        key={value.value}
                        className={
                          'flex flex-col p-2 bg-white border-2 min-w-32 rounded-lg min-w-[120px]'
                        }>
                        <div className={'flex justify-between text-xs'}>
                          <span className={'font-bold'}>{value.label}</span>
                          <RemoveIcon
                            className={'cursor-pointer ml-2'}
                            onClick={(e) => {
                              e.preventDefault()
                              setValue(
                                `assigned_users`,
                                getValues(`assigned_users`).filter(
                                  (v) => v.value !== value.value,
                                ),
                              )
                            }}
                          />
                        </div>
                        <span className={'font-medium pt-1 text-xs'}>{value.value}</span>
                      </div>
                    )
                  })}
                </motion.div>
              )}
            </div>
          </div>
        </div>
        <div className={'h-20 items-center flex justify-between border-t-2 py-4'}>
          <Button
            variant={'secondary'}
            disabled={isSubmitting}
            onClick={(e) => {
              e.preventDefault()
              closeModal()
            }}>
            Close
          </Button>
          <Button disabled={isSubmitting} type={'submit'}>
            Create agency
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}

export default AdminAddAgency
